<template>
  <v-dialog
    v-if="showReminderDialog"
    :value="true"
    :persistent="isRegionMandatory"
    max-width="520"
  >
    <v-card>
      <v-card-title>
        {{ dialogTitle }}
      </v-card-title>
      <v-card-text>
        <div class="region-reminder-dialog__text">
          {{ dialogText }}
        </div>
        <region-select
          v-model="region"
          dense
          outlined
          hide-details="auto"
          :regions="regions"
          :region-branches="regionBranches"
          :menu-props="{ maxHeight: 320 }"
          label="Регион"
        />
        <div v-if="errorMessage" class="region-reminder__error">
          {{ errorMessage }}
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="!isRegionMandatory"
          text
          @click="postponeReminder"
        >
          Напомнить позже
        </v-btn>
        <v-spacer/>
        <v-btn
          color="primary"
          depressed
          :loading="saving"
          :disabled="!canSaveRegion"
          @click="saveRegion"
        >
          Сохранить
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import RegionSelect from './RegistrationRegionSelect.vue'

export default {
  name: 'RegionReminderDialog',
  components: { RegionSelect },
  data() {
    return {
      reminderHiddenUntil: null,
      region: '',
      saving: false,
      errorMessage: null,
    }
  },
  computed: {
    ...mapState(['agent', 'regions', 'regionBranches']),
    today() {
      return new Date().toISOString().slice(0, 10)
    },
    needsRegion() {
      return !!(this.agent && !this.agent.region)
    },
    isRegionMandatory() {
      if (!this.agent || !this.agent.regionRequiredUntil) {
        return false
      }
      return this.today >= this.agent.regionRequiredUntil
    },
    showSoftReminder() {
      if (!this.needsRegion || this.isRegionMandatory) {
        return false
      }
      if (!this.reminderHiddenUntil) {
        return true
      }
      return this.today >= this.reminderHiddenUntil
    },
    showReminderDialog() {
      return this.needsRegion && (this.isRegionMandatory || this.showSoftReminder)
    },
    formattedRegionDeadline() {
      if (!this.agent || !this.agent.regionRequiredUntil) {
        return ''
      }
      const [year, month, day] = this.agent.regionRequiredUntil.split('-')
      return `${day}.${month}.${year}`
    },
    dialogTitle() {
      return this.isRegionMandatory
        ? 'Укажите регион, чтобы продолжить работу'
        : 'Укажите регион'
    },
    dialogText() {
      return this.isRegionMandatory
        ? `С ${this.formattedRegionDeadline} регион обязателен для работы в личном кабинете. Укажите его сейчас, чтобы продолжить.`
        : `Вы можете пользоваться личным кабинетом без указания региона до ${this.formattedRegionDeadline}. После этой даты поле станет обязательным.`
    },
    canSaveRegion() {
      return this.isKnownRegionValue && !this.selectedRegionNeedsBranch
    },
    selectedRegionNeedsBranch() {
      return Boolean(this.region && this.regionBranches[this.region] && this.regionBranches[this.region].length)
    },
    isKnownRegionValue() {
      if (!this.region) {
        return false
      }

      if (this.regions.includes(this.region)) {
        return true
      }

      return Object.values(this.regionBranches).some(
        branches => branches.some(branch => branch.value === this.region),
      )
    },
  },
  created() {
    this.reminderHiddenUntil = localStorage.getItem(this.regionReminderStorageKey())
    this.region = this.agent && this.agent.region ? this.agent.region : ''
  },
  methods: {
    postponeReminder() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      const hiddenUntil = tomorrow.toISOString().slice(0, 10)
      this.reminderHiddenUntil = hiddenUntil
      localStorage.setItem(this.regionReminderStorageKey(), hiddenUntil)
    },
    regionReminderStorageKey() {
      const userId = this.agent && this.agent.id ? this.agent.id : 'anonymous'
      const deadline = this.agent && this.agent.regionRequiredUntil ? this.agent.regionRequiredUntil : 'default'
      return `region-reminder-hidden-until:${userId}:${deadline}`
    },
    async saveRegion() {
      if (!this.region) {
        return
      }
      this.saving = true
      this.errorMessage = null
      try {
        await axios.post('/api/update_personal_data/', {
          last_name: this.agent.lastName,
          first_name: this.agent.firstName,
          middle_name: this.agent.middleName,
          email: this.agent.email,
          phone: this.agent.phone,
          region: this.region,
        })
        this.$store.commit('updateAgent', { region: this.region })
      } catch (e) {
        this.errorMessage = e.response && e.response.data ? e.response.data.message : 'Не удалось сохранить регион'
      } finally {
        this.saving = false
      }
    },
  }
}
</script>

<style scoped>
.region-reminder-dialog__text {
  margin-bottom: 16px;
}

.region-reminder__error {
  color: #b00020;
  margin-top: 8px;
}
</style>
